import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    TextField, 
    Button,  
    CircularProgress, 
    FormControlLabel, 
    Checkbox,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const ProductCategoryPage = () => {
    useAuthCheck();
    const { productCategoryId } = useParams();    
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [formValues, setFormValues] = useState({
        productCategory: "",
        description: "",
        isInventory: true,
        isActive: true,
    });

    
    useEffect(() => {     
        const fetchProductCategory = async () => {
            try {
                setLoading(true);

                if (productCategoryId) {
                    const res = await api.get(`/productcategories/${productCategoryId}`);

                    setFormValues({
                        productCategory: res.data.productCategory || "",
                        description: res.data.description || "",
                        isInventory: res.data.isInventory !== undefined ? res.data.isInventory : true,
                        isActive: res.data.isActive !== undefined ? res.data.isActive : true,
                    });
                }
            } catch (error) {
                setError(error);
                toast.error(error?.response?.data?.message || error.message);
            } finally {
                setLoading(false);
            }
        };
        
        fetchProductCategory();
    }, [productCategoryId]); 
    
    const checkoutSchema = yup.object().shape({
        productCategory: yup.string().required('Required'),        
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = productCategoryId ? "PUT" : "POST";
            const url = productCategoryId 
                ? `/productcategories/update/${productCategoryId}` 
                : '/productcategories/new';

            await api({
                method,
                url,
                data: {
                    productCategory: values.productCategory,
                    description: values.description,
                    isInventory: values.isInventory,
                    isActive: values.isActive,
                },
            });

            toast.success(productCategoryId 
                ? 'Product category updated successfully.' 
                : 'Product category created successfully.'
            );

            // onSubmitProps.resetForm();
            navigate("/productcategories");
        } catch (error) {
            setError(error);
            toast.error(error?.response?.data?.message || "Failed to submit product category!");
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Product Category"
                    subtitle={`${loading ? 'Loading...' : productCategoryId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/productcategories")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    gap={2}
                                    sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 12' } }}
                                >                                    
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Product Category"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.productCategory}
                                        name="productCategory"
                                        error={!!touched.productCategory && !!errors.productCategory}
                                        helperText={touched.productCategory && errors.productCategory}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={3}
                                        label="Description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        name="description"
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 6' }}
                                        control={
                                            <Checkbox
                                                checked={values.isInventory}
                                                onChange={handleChange}
                                                name="isInventory"
                                                color="primary"
                                                
                                            />
                                        }
                                        label="Is Inventory"
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 6' }}
                                        control={
                                            <Checkbox
                                                checked={values.isActive}
                                                onChange={handleChange}
                                                name="isActive"
                                                color="primary"
                                                
                                            />
                                        }
                                        label="Is Active"
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {productCategoryId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default ProductCategoryPage;
